import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Statistic,
  Table,
  TimeRangePickerProps,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { StatisticProps } from 'antd/lib';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import CountUp from 'react-countup';
import { Link, useSearchParams } from 'react-router-dom';
import { Fixed2 } from '../../../../common/utils/common.utils';
import {
  SelectAirline,
  SelectAirport,
} from '../../../../components/common/FormItem/SelectCustomFeilds';
import { useLazyGetAirTicketSummaryv2Query } from '../../AirlineWiseSalesReport/endpoints/AirlineWiseSalesReportEndpoints';
import { IAirTicketSummary } from '../Interfaces/AirTicketReport.interfaces';

const AirTicketSummaryV2 = () => {
  const [form] = Form.useForm();
  const [fetchDATA, { data, isLoading }] = useLazyGetAirTicketSummaryv2Query();
  const [page, setPage] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [searchParams, setSearchParams] = useSearchParams();
  const onFinish = (values: any) => {
    console.log(values);
    const { airline_id, date, segment, search, route_search } = values;
    const filterData: any = {
      page: currentPage,
      limit: pageSize,
    };

    const params: any = {};

    if (airline_id) {
      filterData.airline_id = airline_id;
      params.airline_id = airline_id;
    }
    if (segment) {
      filterData.segment = segment;
      params.segment = segment;
    }
    if (search) {
      filterData.search = search;
      params.search = search;
    }
    if (route_search) {
      const cleanedRoute = route_search.split('->').filter(Boolean).join(' - ');
      filterData.route_search = cleanedRoute;
      params.route_search = cleanedRoute;
    }
    if (date?.length) {
      filterData.from_date = dayjs(date[0]).format('YYYY-MM-DD');
      filterData.to_date = dayjs(date[1]).format('YYYY-MM-DD');
      params.from_date = filterData.from_date;
      params.to_date = filterData.to_date;
    }

    params.page = currentPage;
    params.limit = pageSize;

    setSearchParams(params); // updates URL
    fetchDATA(filterData);
  };
  useEffect(() => {
    const airline_id = searchParams.get('airline_id');
    const segment = searchParams.get('segment');
    const search = searchParams.get('search');
    const from_date = searchParams.get('from_date');
    const to_date = searchParams.get('to_date');
    const route_search = searchParams.get('route_search');
    const pageParam = Number(searchParams.get('page') || 1);
    const limitParam = Number(searchParams.get('limit') || 100);

    const initValues: any = {};

    if (airline_id) initValues.airline_id = airline_id;
    if (segment) initValues.segment = segment;
    if (search) initValues.search = search;
    if (route_search) initValues.route_search = route_search;
    if (from_date && to_date) {
      initValues.date = [dayjs(from_date), dayjs(to_date)];
    } else {
      initValues.date = [dayjs(), dayjs()];
    }

    form.setFieldsValue(initValues);

    setPage(pageParam);
    setPageSize(limitParam);
    setCurrentPage(pageParam);

    // Automatically trigger form submission with prefilled values
    form.submit();
  }, []);

  const AirTicketSummaryColv2 = (): ColumnsType<IAirTicketSummary> => [
    {
      title: 'SL',
      render: (_text, _record, index) => (page - 1) * pageSize + index + 1,
      width: 60,
      align: 'center',
    },
    {
      title: (
        <>
          <p className='table-column-title'>Sales Date</p>
          <p className='table-column-title'>Invoice No</p>
        </>
      ),
      render: (record) => (
        <>
          <p>{dayjs(record?.create_date).format('DD MMM YYYY')}</p>
          <Link target='_blank' to={`/airticket/details/${record.invoice_id}`}>
            {record?.invoice_no}
          </Link>
        </>
      ),
    },
    {
      title: (
        <>
          <p className='table-column-title'>Client Name</p>
          <p className='table-column-title'>Mobile No</p>
        </>
      ),
      render: (record) => (
        <>
          <Link
            target='_blank'
            to={
              record.airticket_client_id
                ? `/clients/details/client-${record.airticket_client_id}`
                : `/combineClients/details/${record.airticket_combined_id}`
            }
            state={record.airticket_client_id ? `/clients` : `/combineds`}
          >
            {record.client_name}
          </Link>
          <p>{record?.client_mobile}</p>
        </>
      ),
    },
    {
      title: (
        <>
          <p className='table-column-title'>Ticket No</p>
          <p className='table-column-title'>Airline</p>
        </>
      ),
      render: (record) => (
        <p>
          <Tooltip style={{ cursor: 'pointer' }} placement='topLeft' title={record?.airline_name}>
            {record?.airline_code}
          </Tooltip>
          {record?.airticket_ticket_no && ' - '}
          {record?.airticket_ticket_no}
        </p>
      ),
    },
    {
      title: (
        <>
          <p className='table-column-title'>Journey Date</p>
          <p className='table-column-title'>Return Date</p>
        </>
      ),
      render: (record) => (
        <>
          <p>
            {record.airticket_journey_date
              ? dayjs(record.airticket_journey_date).format('DD MMM YYYY')
              : ''}
          </p>
          <p>
            {record.airticket_return_date
              ? dayjs(record.airticket_return_date).format('DD MMM YYYY')
              : ''}
          </p>
        </>
      ),
    },
    {
      title: 'PNR',
      dataIndex: 'airticket_pnr',
      key: 'airticket_pnr',
    },
    {
      title: 'Routes',
      render: (record) => <p>{record?.airticket_routes}</p>,
    },
    {
      title: 'Segment',
      align: 'right',
      render: (record) => (
        <span>
          {record?.airticket_profit < 0 ? Math.abs(record?.airticket_profit) : ' '}
          {record?.airticket_segment}
        </span>
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'airticket_discount_total',
      align: 'right',
      render: (value) => Fixed2(value).toLocaleString(),
    },
    {
      title: 'Extra Fee',
      dataIndex: 'airticket_extra_fee',
      align: 'right',
      render: (value) => Fixed2(value).toLocaleString(),
    },
    {
      title: 'Client Price',
      dataIndex: 'airticket_client_price',
      align: 'right',
      render: (value) => Fixed2(value).toLocaleString(),
    },
    {
      title: 'Purchase Price',
      dataIndex: 'airticket_purchase_price',
      align: 'right',
      render: (value) => Fixed2(value).toLocaleString(),
    },
    {
      title: 'Profit',
      dataIndex: 'airticket_profit',
      align: 'right',
      render: (value) => (
        <span className={value > 0 ? 'font-bold font-success' : 'font-bold font-warning'}>
          {Fixed2(value).toLocaleString()}
        </span>
      ),
    },
  ];
  const formatter: StatisticProps['formatter'] = (value) => (
    <CountUp end={value as number} separator=',' />
  );
  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Today', value: [dayjs(), dayjs()] },
    { label: 'Yesterday', value: [dayjs().add(-1, 'd'), dayjs()] },
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ];
  return (
    <div>
      <Form
        onFinish={onFinish}
        layout='vertical'
        form={form}
        initialValues={{ date: [dayjs(), dayjs()], airline_id: 'all' }}
      >
        <Row gutter={[5, 2]} align='middle'>
          <SelectAirline
            required
            name='airline_id'
            label='Select Airline'
            size={4}
            showAll
            allowClear={false}
          />
          <SelectAirport
            allowClear={true}
            label='Route/Sector:'
            name='route_search'
            mode='multiple'
            size={4}
            form={form}
            fullNamePath={['route_search']}
          />
          <Col span={6} xs={24} sm={24} md={4} lg={4}>
            <Form.Item label='Segment' name='segment'>
              <Input placeholder='Search by Segment' type='number' allowClear />
            </Form.Item>
          </Col>
          <Col span={6} xs={24} sm={24} md={4} lg={4}>
            <Form.Item label='Search' name='search'>
              <Input placeholder='Search by ticket no./PNR' allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={4}>
            <Form.Item
              name='date'
              label='Select Date'
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                presets={rangePresets}
                format={'DD-MM-YYYY'}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={4}>
            <Form.Item>
              <Button
                type='primary'
                htmlType='submit'
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  marginTop: '28px',
                }}
              >
                Search
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Row gutter={[10, 10]} style={{ marginBottom: '10px' }}>
        <Col span={6}>
          <Card size='small' className='card_shadow'>
            <Statistic
              title={<Typography.Text strong>{'Total Client Price'}</Typography.Text>}
              value={data?.summary?.total_airticket_client_price}
              formatter={formatter}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size='small' className='card_shadow'>
            <Statistic
              title={<Typography.Text strong>{'Total Discount'}</Typography.Text>}
              value={data?.summary?.total_airticket_discount_total}
              formatter={formatter}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card size='small' className='card_shadow'>
            <Statistic
              title={<Typography.Text strong>{'Total Profit'}</Typography.Text>}
              value={data?.summary?.total_airticket_profit}
              formatter={formatter}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size='small' className='card_shadow'>
            <Statistic
              title={<Typography.Text strong>{'Total Purchase Price'}</Typography.Text>}
              value={data?.summary?.total_airticket_purchase_price}
              formatter={formatter}
            />
          </Card>
        </Col>
      </Row>
      <Typography.Text style={{ margin: '5px 0px' }}>
        You have total: {data?.count} rows
      </Typography.Text>

      <Table
        size='small'
        bordered
        rowKey='invoice_id'
        dataSource={data?.data || []}
        columns={AirTicketSummaryColv2()}
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        pagination={{
          total: data?.count || 0,
          current: page,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['100', '200', '500'],
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
            setCurrentPage(newPage);
            form.submit(); // triggers onFinish and URL update
          },
        }}
        summary={() => {
          return (
            <Table.Summary.Row style={{ background: '#031c291f', height: 40 }}>
              <Table.Summary.Cell index={0} colSpan={5}>
                SUMMARY
              </Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={10}></Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={10}></Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={10}></Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={10}>
                {' '}
                {Fixed2(data?.summary?.total_airticket_discount_total).toLocaleString()}
              </Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={10}>
                {Fixed2(data?.summary?.total_airticket_extra_fee).toLocaleString()}
              </Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={6}>
                {' '}
                {Fixed2(data?.summary?.total_airticket_client_price).toLocaleString()}
              </Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={8}>
                {Fixed2(data?.summary?.total_airticket_purchase_price).toLocaleString()}
              </Table.Summary.Cell>
              <Table.Summary.Cell className='font-bold' index={9}>
                <strong style={{ color: 'green' }}>
                  {Fixed2(data?.summary?.total_airticket_profit).toLocaleString()}
                </strong>
              </Table.Summary.Cell>{' '}
            </Table.Summary.Row>
          );
        }}
      />
    </div>
  );
};

export default AirTicketSummaryV2;
